import { DeleteOutlined } from '@ant-design/icons'
import { useInfiniteQuery, useQueries } from '@tanstack/react-query'
import {
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  Flex,
  Form,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'

import {
  useCounterpartServiceGetContractEndpoint,
  useStocksServiceGetAvailableConsumablesEndpointKey,
  useStocksServiceGetAvailableListEndpointKey
} from 'src/api/queries'
import {
  GetConsumableListResponse,
  GetMedicineListResponse,
  InquiryDocumentResponse,
  InventoryDocumentState,
  InventoryItemResponse,
  Permissions,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { Text } from 'src/components'
import { useDebounce, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { useStyles } from './styles'

type Props = {
  form: FormInstance
  isFinished?: boolean
  issuanceData?: InquiryDocumentResponse
}

type InquiryItem = {
  name: string
  id: string
  quantity: number
  internationalNameId: string
  items: unknown[]
}

export const ConsumableListCreate = ({
  form,
  issuanceData,
  isFinished
}: Props) => {
  const [search, setSearch] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<number[]>([])

  const { userData, hasPermission } = useUser()
  const debouncedSearch = useDebounce(search)
  const { t } = useLocales()

  const { styles } = useStyles()
  const palette = useTheme()

  const isQuantityViewOnly =
    (!hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE) && isFinished) ||
    (issuanceData?.state &&
      ![InventoryDocumentState.NEW, InventoryDocumentState.WAITING].includes(
        issuanceData?.state
      ))

  const providerId = Form.useWatch('contractId', form)

  const { data: contractDetails } = useCounterpartServiceGetContractEndpoint(
    {
      id: providerId
    },
    [providerId],
    {
      enabled: !!providerId
    }
  )

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetAvailableListEndpointKey,
      debouncedSearch,
      String(contractDetails?.counterpart?.id)
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getAvailableListEndpoint(
        pageParam,
        50,
        [String(contractDetails?.counterpart?.id)],
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!contractDetails?.counterpart?.id
  })

  const {
    data: consumablesData,
    fetchNextPage: fetchNextConsumablesPage,
    isFetching: isFetchingConsumables,
    hasNextPage: hasNextConsumablesPage
  } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetAvailableConsumablesEndpointKey,
      debouncedSearch,
      String(contractDetails?.counterpart?.id)
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getAvailableConsumablesEndpoint(
        pageParam,
        50,
        [String(contractDetails?.counterpart?.id)],
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetConsumableListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!contractDetails?.counterpart?.id
  })

  const combinedData = useMemo(() => {
    const medicines = medicinesData?.pages.flatMap((page) => page.data) || []
    const consumables =
      consumablesData?.pages.flatMap((page) => page.data) || []

    const allItems = [
      ...medicines.map((item) => ({
        type: 'medicine',
        label: `${item?.internationalName?.name} ${item?.doses}`,
        value: item?.id,
        item
      })),
      ...consumables.map((item) => ({
        type: 'consumable',
        label: item?.internationalName?.name,
        value: item?.id,
        item
      }))
    ]

    const seen = new Set<string>()
    const uniqueByInternationalName = allItems?.filter(({ item }) => {
      const id = item?.internationalName?.id
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })

    return uniqueByInternationalName
  }, [medicinesData, consumablesData])

  const list = Form.useWatch('inquiryItems', form) || []

  const filteredCombinedData = useMemo(() => {
    const selectedIds = new Set(list.map((item: InquiryItem) => item.id))

    return combinedData.filter((data) => !selectedIds.has(data.value))
  }, [combinedData, list])

  const onSelect = (value: string) => {
    const selectedItem = combinedData.find((item) => item.value === value)

    const prefixes =
      selectedItem?.item?.internationalName?.activeSubstances?.map(
        (item) => item?.prefix
      )

    const prefixLabel = (prefixes ?? [])
      .filter((p) => p?.trim() !== '')
      .join('+')

    if (selectedItem) {
      const newInquiryItem = {
        name: `${selectedItem?.label}${prefixLabel ? ` (${prefixLabel})` : ''}`,
        id: selectedItem.value,
        internationalNameId: selectedItem?.item?.internationalName?.id,
        form: {
          id: selectedItem?.item?.form?.id,
          name: selectedItem?.item?.form?.name
        },
        quantity: 0,
        items: [],
        item: selectedItem.item
      }

      form?.setFieldsValue({ inquiryItems: [...list, newInquiryItem] })
      setSearch('')
    }
  }

  const columns: TableColumnsType = useMemo(
    () => [
      {
        key: 'barCode',
        title: t('stock_applications.series'),
        dataIndex: 'barCode'
      },
      {
        key: 'name',
        title: t('common.commercial_naming'),
        render: ({ product, doses }) => (
          <Text color={palette['blue-6']}>
            {product?.name} {doses}
          </Text>
        )
      },
      {
        key: 'quantity',
        title: t('common.quantity'),
        dataIndex: 'inQuantity'
      },
      {
        key: 'expirationDate',
        title: t('common.expiration_date'),
        render: ({ expirationDate }) =>
          dayjs(expirationDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'currentQuantity',
        title: t('stock_applications.in_stock'),
        dataIndex: 'currentQuantity'
      }
    ],
    [t, palette]
  )

  const inventoryQueries = useQueries({
    queries: list?.map((item: InquiryItem, index: number) => ({
      queryKey: [
        'inventory',
        String(userData?.currentLegalEntityId),
        String(item?.internationalNameId)
      ],
      queryFn: async () => {
        const response = await StocksService.getInventoryListEndpoint(
          1,
          DEFAULT_PER_PAGE,
          undefined,
          undefined,
          undefined,
          undefined,
          [String(userData?.currentLegalEntityId)],
          [String(item?.internationalNameId)]
        )

        return response.data
      },
      enabled: expandedKeys.includes(index)
    }))
  })

  const onExpand = useCallback((keys: string | string[]) => {
    setExpandedKeys(Array.isArray(keys) ? keys.map(Number) : [Number(keys)])
  }, [])

  const totalQuantities = useMemo(() => {
    return inventoryQueries.map((inventoryQuery) => {
      const data = inventoryQuery?.data as InventoryItemResponse[]
      if (data) {
        return data.reduce(
          (acc: number, item: InventoryItemResponse) =>
            acc + (item.currentQuantity || 0),
          0
        )
      } else {
        return 0
      }
    })
  }, [inventoryQueries])

  return (
    <Card
      title={t('stock_applications.requested_medicine')}
      className={styles.applicationTable}
    >
      <Form.List name="inquiryItems">
        {(fields, { remove }) => (
          <>
            {fields.map(({ key, name }) => {
              const totalQuantity = totalQuantities[key]

              const inventoryQuery = inventoryQueries[
                name
              ] as GetConsumableListResponse

              const modifiedQuantity = form.getFieldValue([
                'inquiryItems',
                name,
                'modifiedQuantity'
              ])

              return (
                <Collapse
                  key={key}
                  className={styles.collapseItem}
                  expandIconPosition="end"
                  activeKey={expandedKeys}
                  onChange={onExpand}
                  items={[
                    {
                      key: key,
                      label: (
                        <Flex justify="space-between" align="center" gap={24}>
                          <Flex
                            align="center"
                            justify="space-between"
                            gap={16}
                            wrap
                            className={styles.applicationTableItem}
                          >
                            <Flex wrap>
                              <Text>
                                {form.getFieldValue([
                                  'inquiryItems',
                                  name,
                                  'name'
                                ])}
                              </Text>
                            </Flex>
                            <Flex gap={8} align="center">
                              <Text>
                                {t('stock_applications.needed_quantity')}
                              </Text>
                              <Form.Item
                                name={[name, 'quantity']}
                                rules={requiredRules?.concat({
                                  type: 'number',
                                  min: 1,
                                  message: t('common.min_one_quantity')
                                })}
                              >
                                <InputNumber
                                  min={1}
                                  className="full-width"
                                  disabled={isQuantityViewOnly}
                                />
                              </Form.Item>

                              <Divider type="vertical" />

                              {modifiedQuantity > 0 && (
                                <>
                                  <Text>
                                    {t('stock_applications.approved_quantity')}
                                  </Text>
                                  {modifiedQuantity}
                                </>
                              )}
                              <Divider type="vertical" />

                              <Space size={4}>
                                {t('stock_applications.inStoc')}(
                                <Text>
                                  {form.getFieldValue([
                                    'inquiryItems',
                                    name,
                                    'form',
                                    'name'
                                  ])}
                                </Text>
                                <Tag color="blue">
                                  {totalQuantity ? `( ${totalQuantity} )` : '-'}
                                </Tag>
                              </Space>
                            </Flex>
                          </Flex>

                          {!isFinished && (
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                            />
                          )}
                        </Flex>
                      ),
                      children: (
                        <Card
                          className={classNames(
                            styles.applicationTableItem,
                            styles.applicationCardItem
                          )}
                        >
                          <Card>
                            {inventoryQuery && inventoryQuery?.isLoading ? (
                              <Spin />
                            ) : inventoryQuery?.data ? (
                              <Table
                                dataSource={
                                  inventoryQuery?.data ?? [
                                    form.getFieldValue([
                                      'inquiryItems',
                                      name,
                                      'items'
                                    ])
                                  ]
                                }
                                rowKey="id"
                                scroll={{ x: 'max-content' }}
                                columns={columns}
                                pagination={false}
                              />
                            ) : (
                              <Empty />
                            )}
                          </Card>
                        </Card>
                      )
                    }
                  ]}
                />
              )
            })}
          </>
        )}
      </Form.List>

      {!isFinished && (
        <Card className={styles.applicationCard}>
          <Select
            allowClear
            showSearch
            value={null}
            onSearch={(value) => setSearch(value)}
            onClear={() => setSearch('')}
            loading={isFetchingConsumables || isFetchingMedicines}
            options={filteredCombinedData}
            filterOption={false}
            onSelect={(value) => value && onSelect(value)}
            placeholder={t('common.search')}
            onPopupScroll={(e) => {
              const target = e.target as HTMLElement
              if (
                target.scrollTop + target.offsetHeight ===
                target.scrollHeight
              ) {
                hasNextMedicinesPage && fetchNextMedicinesPage()
                hasNextConsumablesPage && fetchNextConsumablesPage()
              }
            }}
            notFoundContent={
              isFetchingConsumables || isFetchingMedicines ? (
                <Flex justify="center">
                  <Spin spinning />
                </Flex>
              ) : (
                <Empty />
              )
            }
          />
        </Card>
      )}
    </Card>
  )
}
